/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { dateUtils } from '@opengeoweb/shared';
import type {
  LayerOptions,
  RefTimeDimensionMode,
  RefTimeDimensionModes,
} from '.';
import type WMJSDimension from './WMJSDimension';

/**
 * Determine if the time values have a special sequencing for certain services.
 * Please check https://gitlab.com/opengeoweb/geoweb-assets/-/issues/4311 for details.
 * @param layer
 * @returns
 */
export const wmDetermineDimensionMode = (
  layer: LayerOptions,
  refTimeDimensionModes?: RefTimeDimensionModes,
): RefTimeDimensionMode | undefined => {
  return refTimeDimensionModes?.find((timeMode: RefTimeDimensionMode) => {
    if (
      layer.service &&
      layer.name &&
      new RegExp(timeMode.servicematch, 'i').test(layer?.service) &&
      new RegExp(timeMode.layermatch, 'i').test(layer?.name)
    ) {
      return timeMode;
    }
    return undefined;
  });
};

/**
 * Based on https://gitlab.com/opengeoweb/geoweb-assets/-/issues/4311
 * @param wmDimension
 * @param referenceTimeValueToSet
 */
export const wmDimensionHandleCustomTimeFunction = (
  wmDimension: WMJSDimension,
  referenceTimeValueToSet: string,
): void => {
  if (!wmDimension.refTimeDimensionMode) {
    return;
  }
  const newStartValueTime = new Date(referenceTimeValueToSet);
  const referenceTimeKey =
    newStartValueTime.getUTCHours() * 60 + newStartValueTime.getUTCMinutes();
  const ruleSet = wmDimension.refTimeDimensionMode?.ruleset;
  const validRule = ruleSet?.find((rule) => {
    return (
      rule.moduloHH.find((smoduloHH) => {
        const mod = smoduloHH * 60;
        return referenceTimeKey % (60 * 24) === mod;
      }) !== undefined
    );
  });
  const relSteps = validRule?.relativeSteps || [];
  const newValues: Date[] = relSteps.map((relativeSecond): Date => {
    return dateUtils.add(new Date(newStartValueTime.getTime()), {
      seconds: relativeSecond,
    });
  });
  const newValues1: string[] = [];
  newValues.forEach((dt) =>
    newValues1.push(dt.toISOString().replace('.000Z', 'Z')),
  );

  wmDimension.reInitializeValues(newValues1.join(','), true);
  wmDimension.setClosestValue();
};
